import React, { useState, useEffect } from 'react';
import { Calendar, Plus, Trash2, Loader2, AlertCircle } from 'lucide-react';
import { getHolidays, createHoliday, withdrawHoliday } from '../../services/adminService';
import './HolidayAdmin.css';

const HolidayAdmin = () => {
    const [holidays, setHolidays] = useState([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [formData, setFormData] = useState({ name: '', date: '' });
    
    // Modal state
    const [withdrawModal, setWithdrawModal] = useState({ isOpen: false, holidayId: null, reason: '' });

    useEffect(() => {
        fetchHolidays();
    }, []);

    const fetchHolidays = async () => {
        try {
            const data = await getHolidays();
            const sortedHolidays = data.sort((a, b) => new Date(a.date) - new Date(b.date));
            setHolidays(sortedHolidays);
        } catch (err) {
            console.error('Failed to fetch holidays:', err);
            setError('Failed to load holidays.');
        } finally {
            setLoading(false);
        }
    };

    const handleAddHoliday = async (e) => {
        e.preventDefault();
        setError(null);
        if (!formData.name || !formData.date) {
            setError('Please provide both name and date.');
            return;
        }

        try {
            setSubmitting(true);
            await createHoliday(formData);
            setFormData({ name: '', date: '' });
            await fetchHolidays();
        } catch (err) {
            console.error('Failed to create holiday:', err);
            setError('Failed to add holiday.');
        } finally {
            setSubmitting(false);
        }
    };

    const openWithdrawModal = (id) => {
        setWithdrawModal({ isOpen: true, holidayId: id, reason: '' });
    };

    const closeWithdrawModal = () => {
        setWithdrawModal({ isOpen: false, holidayId: null, reason: '' });
    };

    const handleWithdraw = async () => {
        if (!withdrawModal.reason.trim()) {
            setError('Please provide a reason for withdrawal.');
            return;
        }

        try {
            setSubmitting(true);
            await withdrawHoliday(withdrawModal.holidayId, withdrawModal.reason);
            closeWithdrawModal();
            await fetchHolidays();
        } catch (err) {
            console.error('Failed to withdraw holiday:', err);
            setError('Failed to withdraw holiday.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="ems-admin-page">
            <div className="ems-admin-container">
                <div className="ems-header-section">
                    <div>
                        <h1 className="ems-page-title">Manage Holidays</h1>
                        <p className="ems-page-subtitle">Add or remove company-wide holidays for the calendar.</p>
                    </div>
                </div>

                {error && (
                    <div style={{ marginBottom: '1.5rem', padding: '1rem', background: '#FEE2E2', color: '#DC2626', borderRadius: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <AlertCircle size={20} />
                        <span>{error}</span>
                    </div>
                )}

                <div className="holiday-content-layout">
                    {/* Add Holiday Form */}
                    <div className="holiday-form-card">
                        <h2 className="holiday-form-title">Add New Holiday</h2>
                        <form onSubmit={handleAddHoliday}>
                            <div className="holiday-form-group">
                                <label>Holiday Name</label>
                                <input
                                    type="text"
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    placeholder="e.g. New Year's Day"
                                    className="holiday-form-input"
                                    disabled={submitting}
                                    required
                                />
                            </div>
                            <div className="holiday-form-group">
                                <label>Date</label>
                                <input
                                    type="date"
                                    value={formData.date}
                                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                                    className="holiday-form-input"
                                    disabled={submitting}
                                    required
                                />
                            </div>
                            <button type="submit" className="holiday-submit-btn" disabled={submitting}>
                                {submitting ? <Loader2 size={20} className="animate-spin" /> : <Plus size={20} />}
                                Add Holiday
                            </button>
                        </form>
                    </div>

                    {/* Holidays List */}
                    <div className="holiday-list-card">
                        <h2 className="holiday-list-title">All Holidays</h2>
                        {loading ? (
                            <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}>
                                <Loader2 className="animate-spin" style={{ color: '#1E88E5' }} size={32} />
                            </div>
                        ) : holidays.length === 0 ? (
                            <div className="empty-holidays">
                                <Calendar size={48} style={{ margin: '0 auto 1rem auto', opacity: 0.5 }} />
                                <p>No holidays have been added yet.</p>
                            </div>
                        ) : (
                            <ul className="holiday-list-items">
                                {holidays.map((h) => {
                                    const d = new Date(h.date);
                                    const dateStr = `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
                                    
                                    return (
                                        <li key={h._id} className={`holiday-list-item ${h.status === 'withdrawn' ? 'withdrawn' : ''}`}>
                                            <div>
                                                <div className="holiday-details">
                                                    <span className="holiday-date-badge">{dateStr}</span>
                                                    <span className="holiday-name">{h.name}</span>
                                                    <span className="holiday-day">({h.day})</span>
                                                </div>
                                                {h.status === 'withdrawn' && (
                                                    <span className="holiday-withdrawn-reason">Reason: {h.withdrawnReason}</span>
                                                )}
                                            </div>
                                            {h.status !== 'withdrawn' && (
                                                <button 
                                                    onClick={() => openWithdrawModal(h._id)} 
                                                    className="holiday-delete-btn"
                                                    title="Withdraw Holiday"
                                                >
                                                    <Trash2 size={18} />
                                                </button>
                                            )}
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </div>
                </div>
            </div>

            {/* Withdraw Modal */}
            {withdrawModal.isOpen && (
                <div className="holiday-modal-overlay">
                    <div className="holiday-modal">
                        <h3 className="holiday-modal-title">Withdraw Holiday</h3>
                        <p className="holiday-modal-text">Are you want delete this leave?</p>
                        
                        <div className="holiday-form-group" style={{ marginBottom: 0 }}>
                            <label>Reason <span style={{color: '#ef4444'}}>*</span></label>
                            <input
                                type="text"
                                value={withdrawModal.reason}
                                onChange={(e) => setWithdrawModal({ ...withdrawModal, reason: e.target.value })}
                                placeholder="Why is this holiday being withdrawn?"
                                className="holiday-form-input"
                                disabled={submitting}
                                autoFocus
                            />
                        </div>

                        <div className="holiday-modal-actions">
                            <button 
                                onClick={closeWithdrawModal} 
                                className="holiday-btn-cancel"
                                disabled={submitting}
                            >
                                Cancel
                            </button>
                            <button 
                                onClick={handleWithdraw} 
                                className="holiday-btn-confirm"
                                disabled={submitting || !withdrawModal.reason.trim()}
                            >
                                {submitting ? <Loader2 size={16} className="animate-spin" /> : 'Confirm'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default HolidayAdmin;
